import { z } from 'zod'
// the export gates (a type import only: exports.ts reads SENSITIVE_ATTACHMENT_KINDS from here, so nothing of exports.ts runs before this file)
import type { ExportGate } from './exports.ts'
import { areaTypes, bothLabels, instantText, isoDay, localTime, note300, paramDefs, snake, someName, someText, uuid } from './primitives.ts'
import { taskTypeDefineV1, taskTypeDefineV2, taskTypeUpdateV1, taskTypeUpdateV2, taskTypeUpdateV3 } from './taskTypes.ts'
import { courseNoticeApproveV1, courseNoticeRetireV1, courseNoticeUpdateV1, courseNoticeWriteV1 } from './courseStatus.ts'
import { flagSettingsSetV1, pinCorrectV1, pinExclusionCloseV1, pinExclusionSetV1, pinPlaceV1 } from './pins.ts'
import { cuttingSetupRecordV1 } from './cuttingSetup.ts'
import { areaSetPlayBearingV1 } from './playBearing.ts'
import { jobConfirmSettingsV1 } from './settingsConfirmation.ts'
import { irrigationCorrectRunV2, irrigationFaultAcknowledgeV1, irrigationFaultAssignV1, irrigationFaultLinkJobV1, irrigationRecordRunV2, waterUpdateProfileV2 } from './irrigation.ts'
import { observationLinkJobV1, observationRecordV1 } from './observations.ts'
// M16a review, finding 24: a procedure version's pictures are the publisher's own command (procedures.ts; 20261205100050)
import { documentIllustrateV1, documentSetAskReadingV1, procedureAcknowledgeV1 } from './procedures.ts'
import { productUpdateV2, stockConsumeV1, stockLocationArchiveV1, stockLocationDefineV1, stockReceiveV1, stockRefuelV1, stockReturnV1, stockReverseV1, stockTransferV1, stockWasteV1, workorderAddPartV2, workorderRemovePartV2 } from './stock.ts'
import { productReviseV2, qualificationTypeDefineV2, treatmentCancelPlanV1, treatmentCorrectV1, treatmentPlanV1, treatmentRecordApplicationV1, treatmentUpdatePlanV1, treatmentVerifyV1 } from './treatments.ts'
// ---- M15a (docs/M15.md rule 10; M16 rule 8): each part's schema file under src/ imports its building blocks from primitives.ts and is imported HERE by its part
// (an additive line, listed in docs/m15-requests/<part>.md) or by Z's close: stock.ts (A: the ledger with fuel; the on-hand twin too), treatments.ts and nutrients.ts (C)
// ---- M16b (docs/M16.md rule 8; the b opening): each part's schema file imported HERE by its part, an additive line listed in docs/m16-requests/<part>.md:
// measurementsV2.ts (D: measurement.record@2), infra.ts (E), incidents.ts (G), procedures.ts (I2: its new schemas join the file I1's fix began; H has no command)
// M16-E: what was done on an infrastructure feature (infra.ts)
import { infraRecordMaintenanceV1 } from './infra.ts'
// M16-D: a reading with the cell of the club's grid (measurementsV2.ts: measurement.record@2)
import { measurementRecordV2 } from './measurementsV2.ts'
// M16-G: the incident register (incidents.ts)
import { incidentAddActionV1, incidentAssignV1, incidentCloseV1, incidentReportV1 } from './incidents.ts'
// ---- M15b (docs/M15.md rule 10; the b opening): likewise, listed in docs/m15-requests/<part>.md: stockCount.ts (B), documents.ts (V: attachment.register@2), finance.ts (E1)
// M15-B: the physical count (stockCount.ts)
import { stockCountCloseV1, stockCountLineV1, stockCountOpenV1 } from './stockCount.ts'
// M15-V: the document vault (documents.ts: two schema FACTORIES called below with this file's own lists of kinds, and the retirement)
import { attachmentRegisterV2Of, documentPublishVersionV2Of, documentRetireVersionV1 } from './documents.ts'
// M15-E1: the machine's costs (finance.ts: three Y* intents of the direction)
import { assetCostRecordV1, assetCostVoidV1, assetFinancialSetV1 } from './finance.ts'
// ---- M24 (docs/M24.md rule 12; the opening): each part's schema file under src/ imports its building blocks from primitives.ts and is imported HERE by its part (an
// additive line, listed in docs/m24-requests/<part>.md) or by Z's close: connectors.ts (C: connector.define@1, connector.update@1), weather.ts (F: forecast.ingest@1,
// the first command of kind 'system', and the reader's file payload forecastFileV1 for CONNECTOR_INPUT_SCHEMAS below), irrigation.ts (M: water.set_profile@2,
// water.update_profile@3 with the altitude)
// M24-C: the connector frame (connectors.ts: two online intents of the direction; no second factor since 2026-10-28, 20261228100000)
import { connectorDefineV1, connectorUpdateV1 } from './connectors.ts'
// M24-M: the course's altitude (irrigation.ts: water.set_profile@2, water.update_profile@3)
import { waterSetProfileV2, waterUpdateProfileV3 } from './irrigation.ts'
// M24-F: the forecast (weather.ts: forecast.ingest@1, the first command of kind 'system'; forecastFileV1, dwd_mosmix_l's file payload)
import { forecastFileV1, forecastIngestV1 } from './weather.ts'
// M16: the building blocks moved to primitives.ts so that the parts' schema files can share them (a file commands.ts imports cannot import commands.ts back)
export { PARAM_KINDS } from './primitives.ts'

/** person.create v1 (ARCHITECTURE 7.3). Mirrors core.command_type.payload_schema in the migrations. */
export const personCreateV1 = z.strictObject({
  person_id: uuid,
  display_name: z.string().min(1).max(120),
  preferred_locale: z.enum(['fr', 'en']).optional(),
})

export const ASSET_KINDS = ['machine', 'vehicle', 'attachment', 'component', 'tool', 'fixed'] as const
export type AssetKind = (typeof ASSET_KINDS)[number]

/** asset.register v1 (ARCHITECTURE 7.4). Only the identity of the thing is required: no modem, vendor account, meter or
 *  serial (GK-003). Mirrors core.command_type.payload_schema. */
export const assetRegisterV1 = z.strictObject({
  asset_id: uuid,
  readable_tag: z.string().regex(/^[A-Za-z0-9][A-Za-z0-9-]{1,31}$/),
  kind: z.enum(ASSET_KINDS),
  asset_type_code: z.string().max(40).optional(),
  make: z.string().max(80).optional(),
  model: z.string().max(80).optional(),
  serial_no: z.string().max(80).optional(),
  year: z.number().int().min(1900).max(2100).optional(),
  acquired_on: z.string().regex(/^[0-9]{4}-[0-9]{2}-[0-9]{2}$/).optional(),
})

export type PersonCreateV1 = z.infer<typeof personCreateV1>
export type AssetRegisterV1 = z.infer<typeof assetRegisterV1>



// ---------------------------------------------------------------------------------------------------------------------
// M2: fleet register (ARCHITECTURE 7.4). Each schema mirrors core.command_type.payload_schema in the migrations; the contract
// check (ops/contracts/check.mjs) runs the same fixtures through both.
// ---------------------------------------------------------------------------------------------------------------------
const kind = z.enum(ASSET_KINDS)
const provider = z.string().regex(/^[a-z][a-z0-9_]{1,30}$/)

export const assetTypeDefineLocalV1 = z.strictObject({
  type_id: uuid,
  code: z.string().regex(/^[a-z][a-z0-9_]{1,39}$/),
  kind,
  label: z.strictObject({ fr: z.string().min(1).max(80), en: z.string().min(1).max(80) }),
  requires_prestart_check: z.boolean().optional(),
})

export const assetUpdateV1 = z.strictObject({
  asset_id: uuid,
  expected_revision: z.number().int().min(1),
  set: z.strictObject({
    readable_tag: z.string().regex(/^[A-Za-z0-9][A-Za-z0-9-]{1,31}$/).optional(),
    kind: kind.optional(),
    asset_type_code: z.string().max(40).nullable().optional(),
    make: z.string().max(80).nullable().optional(),
    model: z.string().max(80).nullable().optional(),
    serial_no: z.string().max(80).nullable().optional(),
    year: z.number().int().min(1900).max(2100).nullable().optional(),
    acquired_on: z.string().regex(/^[0-9]{4}-[0-9]{2}-[0-9]{2}$/).nullable().optional(),
  }).refine((o) => Object.keys(o).length >= 1, 'at least one field'),
})

export const assetRetireV1 = z.strictObject({ asset_id: uuid, reason: z.string().max(200).optional() })

export const externalIdMapV1 = z.strictObject({
  mapping_id: uuid, asset_id: uuid, provider, provider_org_ref: z.string().max(120).optional(), external_id: z.string().min(1).max(120),
})
export const externalIdUnmapV1 = z.strictObject({ mapping_id: uuid })

export const capabilityDeclareV1 = z.strictObject({
  capability_id: uuid, asset_id: uuid, capability_code: z.string().max(60), provider,
  supported_fields: z.array(z.string().max(40)).max(20).optional(),
})

export const componentMountV1 = z.strictObject({
  mount_id: uuid, component_id: uuid, host_id: uuid, position_code: z.string().regex(/^[a-z0-9_]{1,30}$/).optional(),
})
export const componentUnmountV1 = z.strictObject({ mount_id: uuid })

/** Import (GK-004): the CLIENT parses the file; the server normalises. `cells` are raw strings keyed by the file's own headers. */
export const IMPORT_FIELDS = ['readable_tag', 'kind', 'asset_type_code', 'make', 'model', 'serial_no', 'year', 'acquired_on', 'current_hours'] as const
export type ImportField = (typeof IMPORT_FIELDS)[number]
const header = z.string().max(120)
export const assetImportStageV1 = z.strictObject({
  batch_id: uuid,
  file_sha256: z.string().regex(/^[0-9a-f]{64}$/),
  file_name: z.string().min(1).max(200),
  source: z.enum(['csv', 'xlsx']),
  mapping: z.strictObject({
    readable_tag: header.optional(), kind: header.optional(), asset_type_code: header.optional(), make: header.optional(), model: header.optional(),
    serial_no: header.optional(), year: header.optional(), acquired_on: header.optional(), current_hours: header.optional(),
  }),
  defaults: z.strictObject({ kind: kind.optional(), asset_type_code: z.string().max(40).optional() }).optional(),
  kind_map: z.record(z.string(), kind).refine((o) => Object.keys(o).length <= 60, 'at most 60 entries').optional(),
  total_rows: z.number().int().min(1).max(5000),
  chunk_no: z.number().int().min(1).max(100),
  chunk_count: z.number().int().min(1).max(100),
  rows: z.array(z.strictObject({
    row_no: z.number().int().min(1).max(5000),
    tag: z.string().max(64).optional(),
    cells: z.record(z.string(), z.string().max(500)).refine((o) => Object.keys(o).length <= 60, 'at most 60 cells'),
  })).min(1).max(200),
})
export const assetImportCommitV1 = z.strictObject({
  batch_id: uuid,
  decisions: z.array(z.strictObject({
    row_no: z.number().int().min(1).max(5000), decision: z.enum(['create', 'skip']),
    readable_tag: z.string().regex(/^[A-Za-z0-9][A-Za-z0-9-]{1,31}$/).optional(), kind: kind.optional(),
  })).max(5000).optional(),
})

export type AssetTypeDefineLocalV1 = z.infer<typeof assetTypeDefineLocalV1>
export type AssetUpdateV1 = z.infer<typeof assetUpdateV1>
export type AssetImportStageV1 = z.infer<typeof assetImportStageV1>
export type AssetImportCommitV1 = z.infer<typeof assetImportCommitV1>


// ---------------------------------------------------------------------------------------------------------------------
// M3: meters and service plans (ARCHITECTURE 7.4, 7.5). Same rule as above: each schema mirrors core.command_type.payload_schema and
// the contract check runs the same fixtures through both. Only meter.record is a field fact the device projects offline.
// ---------------------------------------------------------------------------------------------------------------------
export const METER_KINDS = ['engine_hours', 'odometer', 'cycles'] as const
export type MeterKind = (typeof METER_KINDS)[number]
const instant = z.string().regex(/^[0-9]{4}-[0-9]{2}-[0-9]{2}T[0-9:.]+(Z|[+-][0-9]{2}:?[0-9]{2})$/)
const meterValue = z.number().min(0).max(9999999.99)

export const meterDefineSlotV1 = z.strictObject({
  slot_id: uuid, instance_id: uuid, asset_id: uuid, kind: z.enum(METER_KINDS), baseline_value: meterValue,
})
export const meterRecordV1 = z.strictObject({
  observation_id: uuid, slot_id: uuid, value: meterValue, observed_at: instant.optional(), assumed_instance_id: uuid.optional(),
})
export const meterRecordEstimateV1 = z.strictObject({
  observation_id: uuid, slot_id: uuid, value: meterValue, estimate_method: z.string().min(1).max(80), observed_at: instant.optional(),
})
export const meterCorrectV1 = z.strictObject({
  observation_id: uuid, corrects_observation_id: uuid, value: meterValue, reason: z.string().min(1).max(300),
})
export const meterReplaceV1 = z.strictObject({
  instance_id: uuid, slot_id: uuid, final_observation_id: uuid, final_value: meterValue, new_baseline_value: meterValue,
  removed_at: instant.optional(), reason: z.string().max(300).optional(),
})
export const meterReviewV1 = z.strictObject({
  observation_id: uuid, decision: z.enum(['accept', 'reject']), note: z.string().max(300).optional(),
})

export const TRIGGER_LOGICS = ['OR', 'AND', 'CALENDAR_ONLY', 'METER_ONLY'] as const
export const CALENDAR_UNITS = ['day', 'week', 'month', 'year'] as const
export type TriggerLogic = (typeof TRIGGER_LOGICS)[number]
export type CalendarUnit = (typeof CALENDAR_UNITS)[number]
/** The rule combinations (I10) are checked by the server and by a table CHECK; the form builds only valid ones. */
export const servicePlanPublishV1 = z.strictObject({
  plan_id: uuid, version_id: uuid, name: z.string().min(1).max(120).optional(),
  asset_id: uuid.optional(), asset_type_code: z.string().min(1).max(40).optional(),
  trigger_logic: z.enum(TRIGGER_LOGICS),
  calendar_every: z.number().int().min(1).max(1000).optional(), calendar_unit: z.enum(CALENDAR_UNITS).optional(), calendar_lead_days: z.number().int().min(0).max(365).optional(),
  meter_slot_kind: z.enum(METER_KINDS).optional(), meter_interval: z.number().gt(0).max(9999999.99).optional(), meter_lead: meterValue.optional(),
  source: z.enum(['manufacturer', 'local_approved']), evidence_note: z.string().min(1).max(500),
})
export const serviceEventRecordV1 = z.strictObject({
  event_id: uuid, asset_id: uuid, plan_id: uuid, performed_at: instant.optional(), note: z.string().max(500).optional(),
  meter_value: meterValue.optional(), observation_id: uuid.optional(),
})

export type MeterDefineSlotV1 = z.infer<typeof meterDefineSlotV1>
export type MeterRecordV1 = z.infer<typeof meterRecordV1>
export type MeterRecordEstimateV1 = z.infer<typeof meterRecordEstimateV1>
export type MeterCorrectV1 = z.infer<typeof meterCorrectV1>
export type MeterReplaceV1 = z.infer<typeof meterReplaceV1>
export type MeterReviewV1 = z.infer<typeof meterReviewV1>
export type ServicePlanPublishV1 = z.infer<typeof servicePlanPublishV1>
export type ServiceEventRecordV1 = z.infer<typeof serviceEventRecordV1>


// ---------------------------------------------------------------------------------------------------------------------
// M4: the safety loop (ARCHITECTURE 7.5). inspection.submit and defect.raise are SAFETY FACTS (offline, never refused for staleness,
// a critical defect always ends with a server restriction); release and downgrade are online-only behind a fresh one-time code.
// ---------------------------------------------------------------------------------------------------------------------
export const RESPONSE_TYPES = ['ok_defect', 'ok_defect_na', 'numeric'] as const
export const SEVERITIES = ['minor', 'major', 'critical'] as const
export type Severity = (typeof SEVERITIES)[number]
export type ResponseType = (typeof RESPONSE_TYPES)[number]
const itemKey = z.string().regex(/^[a-z0-9][a-z0-9_]{0,39}$/)
const bilingual = z.strictObject({ fr: z.string().min(1).max(200), en: z.string().min(1).max(200) })

export const checklistPublishV1 = z.strictObject({
  template_id: uuid, version_id: uuid, name: z.string().min(1).max(120).optional(),
  asset_type_codes: z.array(z.string().min(1).max(40)).max(40).optional(),
  items: z.array(z.strictObject({
    item_key: itemKey, label: bilingual, response_type: z.enum(RESPONSE_TYPES), unit_code: z.string().max(20).optional(),
    is_critical: z.boolean().optional(), requires_photo: z.boolean().optional(),
  })).min(1).max(200),
})
export const inspectionSubmitV1 = z.strictObject({
  inspection_id: uuid, asset_id: uuid, checklist_version_id: uuid, started_at: instant.optional(), note: z.string().max(500).optional(),
  responses: z.array(z.strictObject({
    item_key: itemKey, response: z.enum(['ok', 'defect', 'na']), numeric_value: z.number().min(-9999999.99).max(9999999.99).optional(),
    note: z.string().max(500).optional(), escalate: z.boolean().optional(),
  })).min(1).max(200),
})
export const defectRaiseV1 = z.strictObject({ defect_id: uuid, asset_id: uuid, severity: z.enum(SEVERITIES), description: z.string().min(1).max(500) })
export const defectTriageV1 = z.strictObject({ event_id: uuid, defect_id: uuid, severity: z.enum(SEVERITIES).optional(), work_order_id: uuid.optional(), note: z.string().max(500).optional() })
export const defectDowngradeV1 = z.strictObject({ event_id: uuid, defect_id: uuid, severity: z.enum(['minor', 'major']), reason: z.string().min(1).max(500) })
export const assetRestrictV1 = z.strictObject({ restriction_id: uuid, asset_id: uuid, reason_kind: z.enum(['manual', 'service']), note: z.string().min(1).max(500) })
export const assetReleaseV1 = z.strictObject({
  asset_id: uuid, addressed_restriction_ids: z.array(uuid).min(1).max(50), note: z.string().min(1).max(500), isolation_attestation: z.boolean(),
})
export const workorderOpenV1 = z.strictObject({ work_order_id: uuid, asset_id: uuid, kind: z.enum(['repair', 'service', 'inspection']), complaint: z.string().max(500).optional() })
export const workorderUpdateV1 = z.strictObject({
  work_order_id: uuid, expected_revision: z.number().int().min(1),
  set: z.strictObject({
    complaint: z.string().max(500).nullable().optional(), diagnosis: z.string().max(1000).nullable().optional(),
    service_performed: z.string().max(1000).nullable().optional(), state: z.enum(['open', 'in_progress', 'awaiting_parts']).optional(),
  }).refine((o) => Object.keys(o).length >= 1, 'at least one field'),
})
export const workorderMarkRepairedV1 = z.strictObject({ work_order_id: uuid, rts_notes: z.string().min(1).max(1000) })
export const workorderCancelV1 = z.strictObject({ work_order_id: uuid, reason: z.string().max(300).optional() })

export type ChecklistPublishV1 = z.infer<typeof checklistPublishV1>
export type InspectionSubmitV1 = z.infer<typeof inspectionSubmitV1>
export type DefectRaiseV1 = z.infer<typeof defectRaiseV1>
export type AssetReleaseV1 = z.infer<typeof assetReleaseV1>


// ---------------------------------------------------------------------------------------------------------------------
// M5: people, access, qualifications, pay, devices (ARCHITECTURE 7.3). Each schema mirrors core.command_type.payload_schema. Every command that
// is online-only and needs a second factor (Y* in 6.3) is registered requires_aal2 + offline_allowed = false in the database.
// ---------------------------------------------------------------------------------------------------------------------
export const LOCALES_M5 = ['fr', 'en'] as const
export const personUpdateProfileV1 = z.strictObject({
  person_id: uuid, expected_revision: z.number().int().min(1),
  set: z.strictObject({ display_name: z.string().min(1).max(120).optional(), preferred_locale: z.enum(LOCALES_M5).optional() }).refine((o) => Object.keys(o).length >= 1, 'at least one field'),
})
export const personUpdateOwnProfileV1 = z.strictObject({ preferred_locale: z.enum(LOCALES_M5) })
export const personLinkLoginV1 = z.strictObject({ person_id: uuid, auth_user_id: uuid })
export const memberGrantRoleV1 = z.strictObject({ membership_id: uuid, person_id: uuid, role_code: z.string().min(1).max(40) })
export const memberRevokeRoleV1 = z.strictObject({ membership_id: uuid })
export const mfaApproveFactorV1 = z.strictObject({ approval_id: uuid, person_id: uuid, factor_id: uuid })
export const mfaRevokeFactorV1 = z.strictObject({ person_id: uuid, factor_id: uuid })
export const deviceEnrollV1 = z.strictObject({ device_id: uuid, auth_user_id: uuid, label: z.string().max(60).optional() })
export const deviceRevokeV1 = z.strictObject({ device_id: uuid })
export const reviewFlagDeviceCommandsV1 = z.strictObject({ run_id: uuid, device_id: uuid, since: instantText })
export const qualificationTypeDefineV1 = z.strictObject({
  type_id: uuid, code: z.string().regex(/^[a-z0-9][a-z0-9_]{0,39}$/), label: z.strictObject({ fr: z.string().min(1).max(120), en: z.string().min(1).max(120) }),
  requires_evidence: z.boolean().optional(), default_validity_days: z.number().int().min(1).max(3660).optional(),
})
export const qualificationRecordV1 = z.strictObject({
  qualification_id: uuid, person_id: uuid, type_id: uuid, valid_from: isoDay, expires_on: isoDay.optional(), evidence_file_id: uuid.optional(), note: z.string().max(300).optional(),
})
export const qualificationRevokeV1 = z.strictObject({ qualification_id: uuid, reason: z.string().min(1).max(300) })
export const qualificationOverrideV1 = z.strictObject({
  override_id: uuid, person_id: uuid, type_id: uuid, scope_kind: z.enum(['job', 'asset_type']), scope_ref: z.string().min(1).max(80), reason: z.string().min(1).max(300),
  valid_from: instantText, valid_until: instantText,
})
export const assetTypeRequirementSetV1 = z.strictObject({ requirement_id: uuid, asset_type_code: z.string().min(1).max(40), type_id: uuid })
export const assetTypeRequirementClearV1 = z.strictObject({ requirement_id: uuid })
export const payRateSetV1 = z.strictObject({ rate_id: uuid, person_id: uuid, amount: z.number().min(0).max(99999.99), unit: z.enum(['hour', 'month']), valid_from: instantText.optional() })
export const personContactSetV1 = z.strictObject({ person_id: uuid, email: z.string().max(200).optional(), phone: z.string().max(40).optional(), address: z.string().max(300).optional() })
/** M11 (ARCHITECTURE 7.3, 16.2): erasing a departed person. Y* (aal2, online, no projector), member.manage; run by POST /admin/people/:id/erase, which then blanks the sign-in account. */
export const personPseudonymiseV1 = z.strictObject({ person_id: uuid })

/** Every command a device may send, by `name@version`. The database registry (core.command_type) is the authority; the
 *  contract test (ops/contracts) checks that both agree on a shared set of fixtures. */
// ---------------------------------------------------------------------------------------------------------------------
// M6: the course and the map (ARCHITECTURE 7.x, 8.6, 11). GeoJSON is checked for SHAPE here and in the database's JSON schema; the real
// validation (valid polygon, limits, kind of shape) is PostGIS's, in the handler.
// ---------------------------------------------------------------------------------------------------------------------
export const AREA_SOURCES = ['survey_gnss', 'device_gps', 'digitised_ortho', 'import', 'sketch'] as const
export const INFRA_SOURCES = ['survey_gnss', 'device_gps', 'digitised_ortho', 'as_built', 'sketch'] as const
export const INFRA_KINDS = ['valve', 'sprinkler', 'satellite', 'controller', 'drain', 'pipe', 'cable', 'manhole', 'other'] as const
export type InfraKind = (typeof INFRA_KINDS)[number]
const geoJson = z.looseObject({ type: z.string(), coordinates: z.array(z.unknown()) })
const geoCode = z.string().regex(/^[A-Za-z0-9][A-Za-z0-9._-]{0,29}$/)
const areaName = z.strictObject({ fr: z.string().min(1).max(120).optional(), en: z.string().min(1).max(120).optional() }).refine((o) => Object.keys(o).length >= 1)
const areaVersion = z.strictObject({
  version_id: uuid, geom: geoJson, source: z.enum(AREA_SOURCES),
  accuracy_m: z.number().min(0).max(1000).nullable().optional(), captured_on: isoDay.nullable().optional(), note: z.string().max(500).nullable().optional(),
})
const sortKey = z.number().int().min(-100000).max(100000)

export const areaCreateV1 = z.strictObject({
  area_id: uuid, parent_id: uuid.nullable().optional(), area_type: z.string().regex(/^[a-z][a-z0-9_]{1,30}$/), code: geoCode.nullable().optional(),
  name: areaName, hole_number: z.number().int().min(1).max(99).nullable().optional(), sort_key: sortKey.optional(), version: areaVersion.nullable().optional(),
})
export const areaUpdateV1 = z.strictObject({
  area_id: uuid, expected_revision: z.number().int().min(1),
  set: z.strictObject({ name: areaName.optional(), code: geoCode.nullable().optional(), hole_number: z.number().int().min(1).max(99).nullable().optional(), sort_key: sortKey.optional(), parent_id: uuid.optional() })
    .refine((o) => Object.keys(o).length >= 1),
})
export const areaReviseV1 = z.strictObject({
  area_id: uuid, version_id: uuid, geom: geoJson, source: z.enum(AREA_SOURCES),
  accuracy_m: z.number().min(0).max(1000).nullable().optional(), captured_on: isoDay.nullable().optional(), note: z.string().max(500).nullable().optional(),
})
export const areaArchiveV1 = z.strictObject({ area_id: uuid })

const infraVersion = z.strictObject({
  version_id: uuid, geom: geoJson, source: z.enum(INFRA_SOURCES), accuracy_m: z.number().min(0).max(1000).nullable().optional(),
  depth_m: z.number().min(0).max(20).nullable().optional(), captured_on: isoDay.nullable().optional(), note: z.string().max(500).nullable().optional(),
})
export const infraCreateV1 = z.strictObject({
  feature_id: uuid, kind: z.enum(INFRA_KINDS), label: z.string().min(1).max(120), code: geoCode.nullable().optional(), area_id: uuid.nullable().optional(),
  note: z.string().max(500).nullable().optional(), version: infraVersion,
})
export const infraUpdateV1 = z.strictObject({
  feature_id: uuid, expected_revision: z.number().int().min(1),
  set: z.strictObject({ kind: z.enum(INFRA_KINDS).optional(), label: z.string().min(1).max(120).optional(), code: geoCode.nullable().optional(), area_id: uuid.nullable().optional(), note: z.string().max(500).nullable().optional() })
    .refine((o) => Object.keys(o).length >= 1),
})
export const infraReviseV1 = z.strictObject({
  feature_id: uuid, version_id: uuid, geom: geoJson, source: z.enum(INFRA_SOURCES), accuracy_m: z.number().min(0).max(1000).nullable().optional(),
  depth_m: z.number().min(0).max(20).nullable().optional(), captured_on: isoDay.nullable().optional(), note: z.string().max(500).nullable().optional(),
})
export const infraRetireV1 = z.strictObject({ feature_id: uuid, reason: z.string().max(300).nullable().optional() })

export const mapLayerPublishV1 = z.strictObject({
  layer_id: uuid, kind: z.enum(['ortho_pmtiles', 'cadastre']), storage_key: z.string().min(10).max(300), sha256: z.string().regex(/^[0-9a-f]{64}$/), bytes: z.number().int().min(1),
  source: z.string().min(1).max(60), capture_year: z.number().int().min(1990).max(2100).nullable().optional(),
  resolution_m: z.number().gt(0).max(100).nullable().optional(), attribution: z.string().min(1).max(200),
  min_zoom: z.number().int().min(0).max(24), max_zoom: z.number().int().min(0).max(24),
  bounds: z.array(z.number()).length(4).nullable().optional(), note: z.string().max(300).nullable().optional(),
})

// ---------------------------------------------------------------------------------------------------------------------
// M7: planning (ARCHITECTURE 7.7, 8.5, 8.10.5). Task types, procedures, job templates and recurrences are the head greenkeeper's data; jobs,
// assignments and availability are what the board changes. Each schema mirrors core.command_type.payload_schema.
// ---------------------------------------------------------------------------------------------------------------------
const someBody = z.strictObject({ fr: z.string().min(1).max(20000).optional(), en: z.string().min(1).max(20000).optional() }).refine((o) => Object.keys(o).length >= 1)
/** The values of a task type's setup parameters: text, numbers, a choice (checked against the task type by the server). */
export const paramValues = z.record(z.string(), z.union([z.string(), z.number()])).refine((o) => Object.keys(o).length <= 20)
const monthDay = z.string().regex(/^(0[1-9]|1[0-2])-(0[1-9]|[12][0-9]|3[01])$/).nullable()
const crewCount = z.number().int().min(0).max(50).nullable()
const group = z.number().int().min(1).max(9)
export const DOCUMENT_KINDS = ['procedure', 'manual', 'sds', 'label', 'policy'] as const
export const RECURRENCE_FREQS = ['daily', 'weekly', 'monthly', 'every_n_days'] as const
export const CANCEL_REASONS = ['weather', 'not_needed', 'duplicate', 'other'] as const
export const AVAILABILITY_STATUSES = ['working', 'partial', 'off'] as const

// the task types' schemas (define@1, update@1, M16's update@2) live in taskTypes.ts
export const documentPublishVersionV1 = z.strictObject({
  document_id: uuid, version_id: uuid, kind: z.enum(DOCUMENT_KINDS).optional(), title: someName, summary: someText.optional(), body: someBody, effective_from: isoDay.optional(),
})
export const jobTemplatePublishV1 = z.strictObject({
  template_id: uuid, version_id: uuid, task_type_code: snake(40).optional(), title: someName, instructions: someText.optional(),
  procedure_document_version_id: uuid.nullable().optional(), default_duration_min: z.number().int().min(5).max(1440).nullable().optional(),
  required_qualification_type_ids: z.array(uuid).max(10).optional(), default_area_ids: z.array(uuid).max(60).optional(),
  default_asset_type_codes: z.array(z.string().min(1).max(40)).max(10).optional(), params: paramValues.optional(), required_crew: crewCount.optional(), sequence_group: group.optional(),
})
export const recurrencePublishV1 = z.strictObject({
  recurrence_id: uuid, version_id: uuid, job_template_id: uuid.optional(), template_version_id: uuid, freq: z.enum(RECURRENCE_FREQS), interval_n: z.number().int().min(1).max(366).optional(),
  by_weekday: z.array(z.number().int().min(1).max(7)).min(1).max(7).nullable().optional(), by_monthday: z.array(z.number().int().min(-1).max(31)).min(1).max(31).nullable().optional(),
  season_start_md: monthDay.optional(), season_end_md: monthDay.optional(), local_start_time: localTime.optional(),
  valid_from: isoDay, valid_to: isoDay.nullable().optional(), exdates: z.array(isoDay).max(400).optional(), rotation: z.array(paramValues).min(1).max(12).nullable().optional(),
})
export const recurrenceArchiveV1 = z.strictObject({ recurrence_id: uuid })
export const jobCreateV1 = z.strictObject({
  job_id: uuid, task_type_code: snake(40).optional(), template_version_id: uuid.optional(), title: someName.optional(), notes: z.string().max(1000).nullable().optional(),
  params: paramValues.optional(), scheduled_local_date: isoDay, scheduled_start_local: localTime.optional(), priority: z.number().int().min(0).max(9).optional(),
  sequence_group: group.optional(), required_crew: crewCount.optional(), area_ids: z.array(uuid).max(60).optional(),
})
export const jobUpdateV1 = z.strictObject({
  job_id: uuid, expected_revision: z.number().int().min(1),
  set: z.strictObject({
    title: someName.optional(), notes: z.string().max(1000).nullable().optional(), params: paramValues.optional(), scheduled_start_local: localTime.optional(),
    priority: z.number().int().min(0).max(9).optional(), sequence_group: group.optional(), required_crew: crewCount.optional(), area_ids: z.array(uuid).max(60).optional(),
  }).refine((o) => Object.keys(o).length >= 1),
})
export const jobRescheduleV1 = z.strictObject({ job_id: uuid, expected_revision: z.number().int().min(1), scheduled_local_date: isoDay, scheduled_start_local: localTime.optional() })
export const jobCancelV1 = z.strictObject({ job_id: uuid, reason: z.enum(CANCEL_REASONS) })
export const jobCopyDayV1 = z.strictObject({ from_date: isoDay, to_date: isoDay, with_crew: z.boolean().optional() })
export const assignmentCreateV1 = z.strictObject({
  assignment_id: uuid, job_id: uuid, person_id: uuid, asset_id: uuid.nullable().optional(), start_area_id: uuid.nullable().optional(), qualification_override_id: uuid.nullable().optional(),
})
export const assignmentRemoveV1 = z.strictObject({ assignment_id: uuid })
export const availabilitySetV1 = z.strictObject({ person_id: uuid, dates: z.array(isoDay).min(1).max(62), status: z.enum(AVAILABILITY_STATUSES) })

// ---------------------------------------------------------------------------------------------------------------------
// M8: doing the work (ARCHITECTURE 7.7, 9.5). Start, pause, resume, switch machine and finish are FACTS a device records offline; manual time too.
// Each schema mirrors core.command_type.payload_schema.
// ---------------------------------------------------------------------------------------------------------------------
export const COVERAGE_LEVELS = ['full', 'partial'] as const
export const JOB_OUTCOMES = ['complete', 'partial'] as const
export const MANUAL_SOURCES = ['manual', 'estimated'] as const
export const jobStartV1 = z.strictObject({
  session_id: uuid, interval_id: uuid, job_id: uuid.optional(), work_order_id: uuid.optional(), asset_id: uuid.optional(), allocation_id: uuid.optional(),
})
export const jobPauseV1 = z.strictObject({ session_id: uuid, asset_id: uuid.optional() })
export const jobResumeV1 = z.strictObject({ session_id: uuid, interval_id: uuid, asset_id: uuid.optional() })
export const jobSwitchAssetV1 = z.strictObject({ session_id: uuid, allocation_id: uuid, asset_id: uuid })
export const jobFinishV1 = z.strictObject({
  session_id: uuid, completion_id: uuid, outcome: z.enum(JOB_OUTCOMES), asset_id: uuid.optional(), notes: z.string().max(1000).nullable().optional(),
  covered: z.array(z.strictObject({ area_id: uuid, coverage: z.enum(COVERAGE_LEVELS).optional() })).max(120).optional(),
})
export const workManualEntryV1 = z.strictObject({
  session_id: uuid, interval_id: uuid, job_id: uuid.optional(), work_order_id: uuid.optional(), person_id: uuid.optional(),
  started_at: instantText, ended_at: instantText, source: z.enum(MANUAL_SOURCES), note: note300.optional(),
})
export const workCorrectV1 = z.strictObject({ interval_id: uuid, new_interval_id: uuid, started_at: instantText, ended_at: instantText, note: note300.optional() })
export const workReviewV1 = z.strictObject({ interval_id: uuid, decision: z.enum(['accepted', 'rejected']), note: note300.optional() })
export const reviewResolveV1 = z.strictObject({ review_item_id: uuid, resolution: z.string().min(1).max(60), note: note300.optional() })
export type JobStartV1 = z.infer<typeof jobStartV1>
export type JobPauseV1 = z.infer<typeof jobPauseV1>
export type JobResumeV1 = z.infer<typeof jobResumeV1>
export type JobSwitchAssetV1 = z.infer<typeof jobSwitchAssetV1>
export type JobFinishV1 = z.infer<typeof jobFinishV1>
export type WorkManualEntryV1 = z.infer<typeof workManualEntryV1>
export type WorkCorrectV1 = z.infer<typeof workCorrectV1>
export type WorkReviewV1 = z.infer<typeof workReviewV1>
export type ReviewResolveV1 = z.infer<typeof reviewResolveV1>

// ---------------------------------------------------------------------------------------------------------------------
// M8: photos (ARCHITECTURE 9.10). Register (a fact, accepted offline: the file is pending), upload the bytes under the device's own login, confirm. The file id is
// v5(org, sha256 hex), the link id v5(file, "type:entity:purpose"), the storage key carries the hash: the same bytes are stored once however often they are sent.
// ---------------------------------------------------------------------------------------------------------------------
// M17: a photo may show a machine (purpose 'photo': the newest one is its portrait).
// M16a (rule 10, ADR-116; 20261201100000): an infrastructure feature (E), a procedure version (I1, purpose 'illustration'), a ground observation (N). Widening
// this list is additive and stays @1 (pgTAP 01 pins that every older kind keeps validating).
// M15a (docs/M15.md rule 7; 20261205100000): a stock movement (A: the delivery note's photo on a receipt; the movement is the club's, matched without a site).
// Release b (the b openings, 20261210100000 and 20261212100000; pgTAP 87): an incident (M16-G: its evidence; its reporter or incident.manage attaches) and a
// machine's cost (M15-E1: the invoice; finance.manage attaches; its PDF through V's documents bucket and attachment.register@2).
export const ATTACHMENT_ENTITY_TYPES = ['fleet.inspection', 'fleet.defect', 'fleet.work_order', 'ops.work_session', 'ops.job_completion', 'fleet.asset', 'geo.infra_feature', 'core.document_version', 'ops.observation', 'stock.movement', 'ops.incident', 'fleet.asset_cost'] as const
export type AttachmentEntityType = (typeof ATTACHMENT_ENTITY_TYPES)[number]
/** The kinds whose files belong to a SENSITIVE record (ADR-119): their links reach no device (the RESTRICTIVE policy gk_sensitive_kinds on core.attachment_link
 *  hides them from every client, the pull included), their bytes are read only by the kind's reader with an approved second factor, and each leaves an export
 *  only under its gate (the link rows: `core.attachment_link`'s rowGate in exports.ts). The twin of `private.attachment_kind_read_perm` (a kind with a reader
 *  in the database is a key here): `npm run test:contracts` and pgTAP 87 hold them equal. A device projects no link of these kinds (sync/projectors.ts). */
export const SENSITIVE_ATTACHMENT_KINDS = { 'ops.incident': 'incidents', 'fleet.asset_cost': 'finance' } as const satisfies Partial<Record<AttachmentEntityType, ExportGate>>
export const isSensitiveAttachmentKind = (kind: string): boolean => Object.hasOwn(SENSITIVE_ATTACHMENT_KINDS, kind)
/** the purpose of a machine's portrait photo */
export const ASSET_PHOTO_PURPOSE = 'photo'
export const ATTACHMENT_MIMES = ['image/jpeg', 'image/png', 'image/webp'] as const
export const MAX_ATTACHMENT_BYTES = 10_485_760
const attPurpose = z.string().regex(/^[a-z][a-z0-9_]{1,29}$/)
export const attachmentRegisterV1 = z.strictObject({
  sha256: z.string().regex(/^[0-9a-f]{64}$/), bytes: z.number().int().min(1).max(MAX_ATTACHMENT_BYTES), mime: z.enum(ATTACHMENT_MIMES), captured_at: instantText,
  entity_type: z.enum(ATTACHMENT_ENTITY_TYPES), entity_id: uuid, purpose: attPurpose.optional(), caption: z.string().max(200).nullable().optional(),
})
export const attachmentConfirmV1 = z.strictObject({ file_id: uuid })
export const attachmentLinkV1 = z.strictObject({ file_id: uuid, entity_type: z.enum(ATTACHMENT_ENTITY_TYPES), entity_id: uuid, purpose: attPurpose.optional(), caption: z.string().max(200).nullable().optional() })
export type AttachmentRegisterV1 = z.infer<typeof attachmentRegisterV1>
export type AttachmentConfirmV1 = z.infer<typeof attachmentConfirmV1>
export type AttachmentLinkV1 = z.infer<typeof attachmentLinkV1>

// ---------------------------------------------------------------------------------------------------------------------
// M9: the catalogue (ARCHITECTURE 7.8). Suppliers, products and their immutable versions, batches, and the parts used on a work order. All intents (the workshop and the storeroom are
// online-first). A density comes with its source; a part is entered in any unit of its dimension (a mass for a volume only through the product's density).
// ---------------------------------------------------------------------------------------------------------------------
export const PRODUCT_KINDS = ['part', 'fertiliser', 'ppp', 'biostimulant', 'seed', 'sand', 'fuel', 'lubricant', 'other'] as const
const catName = z.string().min(1).max(120)
const catText = z.string().max(300).nullable()
const uuidOrNull = uuid.nullable()
const dayOrNull = isoDay.nullable()
export const supplierDefineV1 = z.strictObject({ supplier_id: uuid, name: catName, contact: catText.optional() })
export const supplierUpdateV1 = z.strictObject({
  supplier_id: uuid, expected_revision: z.number().int().min(1),
  set: z.strictObject({ name: catName.optional(), contact: catText.optional(), archived: z.boolean().optional() }).refine((o) => Object.keys(o).length > 0),
})
export const productDefineV1 = z.strictObject({
  product_id: uuid, version_id: uuid, kind: z.enum(PRODUCT_KINDS), name: catName, base_unit_code: z.string().min(1).max(20), supplier_id: uuidOrNull.optional(),
  reference: z.string().max(60).nullable().optional(), amm_number: z.string().max(30).nullable().optional(), density_kg_per_l: z.number().nullable().optional(),
  density_source: z.string().max(200).nullable().optional(), formulation: z.record(z.string(), z.unknown()).optional(), document_version_id: uuidOrNull.optional(),
})
export const productUpdateV1 = z.strictObject({
  product_id: uuid, expected_revision: z.number().int().min(1),
  set: z.strictObject({ name: catName.optional(), supplier_id: uuidOrNull.optional(), reference: z.string().max(60).nullable().optional(), amm_number: z.string().max(30).nullable().optional(), archived: z.boolean().optional() })
    .refine((o) => Object.keys(o).length > 0),
})
export const productReviseV1 = z.strictObject({
  product_id: uuid, version_id: uuid, expected_version_no: z.number().int().min(1), density_kg_per_l: z.number().nullable().optional(), density_source: z.string().max(200).nullable().optional(),
  formulation: z.record(z.string(), z.unknown()).optional(), document_version_id: uuidOrNull.optional(),
})
export const batchRecordV1 = z.strictObject({ batch_id: uuid, product_id: uuid, lot: z.string().min(1).max(60), expiry: dayOrNull.optional(), received_on: dayOrNull.optional(), supplier_id: uuidOrNull.optional() })
export const workorderAddPartV1 = z.strictObject({
  part_id: uuid, work_order_id: uuid, product_id: uuid, quantity: z.number(), unit: z.string().min(1).max(20), batch_id: uuidOrNull.optional(), note: z.string().max(200).nullable().optional(),
})
export const workorderRemovePartV1 = z.strictObject({ part_id: uuid })
export type SupplierDefineV1 = z.infer<typeof supplierDefineV1>
export type SupplierUpdateV1 = z.infer<typeof supplierUpdateV1>
export type ProductDefineV1 = z.infer<typeof productDefineV1>
export type ProductUpdateV1 = z.infer<typeof productUpdateV1>
export type ProductReviseV1 = z.infer<typeof productReviseV1>
export type BatchRecordV1 = z.infer<typeof batchRecordV1>
export type WorkorderAddPartV1 = z.infer<typeof workorderAddPartV1>
export type WorkorderRemovePartV1 = z.infer<typeof workorderRemovePartV1>

// ---------------------------------------------------------------------------------------------------------------------
// M13: the measurement notebook (benchmark GK-030, GK-031, GK-039). A reading is a FACT of the field (offline-first); corrections, targets and the file import are intents. The unit is typed as the
// person typed it (any unit of the type's dimension); the server converts exactly. Mirrors core.command_type.payload_schema.
// ---------------------------------------------------------------------------------------------------------------------
const measType = z.string().regex(/^[a-z][a-z0-9_]{1,40}$/)
const text80 = z.string().max(80).nullable()
const text300 = z.string().max(300).nullable()
const depthMm = z.number().int().min(0).max(2000).nullable()
const unitText = z.string().min(1).max(20)
export const measurementRecordV1 = z.strictObject({
  measurement_id: uuid, measurement_type: measType, area_id: uuidOrNull.optional(), value: z.number(), unit: unitText, observed_at: instant.optional(),
  depth_mm: depthMm.optional(), method: text80.optional(), instrument: text80.optional(), note: text300.optional(),
})
export const measurementCorrectV1 = z.strictObject({ measurement_id: uuid, corrects_measurement_id: uuid, value: z.number(), unit: unitText, reason: z.string().min(1).max(300) })
export const measurementSetTargetV1 = z.strictObject({
  target_id: uuid, measurement_type: measType, area_type_code: z.string().regex(/^[a-z][a-z0-9_]{1,30}$/).nullable().optional(), month_from: z.number().int().min(1).max(12), month_to: z.number().int().min(1).max(12),
  low: z.number().nullable().optional(), high: z.number().nullable().optional(), method: text80.optional(), note: text300.optional(),
})
export const measurementUpdateTargetV1 = z.strictObject({
  target_id: uuid, expected_revision: z.number().int().min(1),
  set: z.strictObject({ low: z.number().nullable().optional(), high: z.number().nullable().optional(), method: text80.optional(), note: text300.optional(), archived: z.boolean().optional() }).refine((o) => Object.keys(o).length > 0),
})
export const MEASUREMENT_IMPORT_MAX_ROWS = 1000
export const measurementImportV1 = z.strictObject({
  batch_id: uuid, file_sha256: z.string().regex(/^[0-9a-f]{64}$/), file_name: z.string().max(200).nullable().optional(),
  rows: z.array(z.strictObject({
    measurement_type: measType, area_id: uuidOrNull.optional(), value: z.number(), unit: unitText, observed_at: instant, depth_mm: depthMm.optional(), method: text80.optional(), instrument: text80.optional(),
  })).min(1).max(MEASUREMENT_IMPORT_MAX_ROWS),
})
export type MeasurementRecordV1 = z.infer<typeof measurementRecordV1>
export type MeasurementCorrectV1 = z.infer<typeof measurementCorrectV1>
export type MeasurementSetTargetV1 = z.infer<typeof measurementSetTargetV1>
export type MeasurementUpdateTargetV1 = z.infer<typeof measurementUpdateTargetV1>
export type MeasurementImportV1 = z.infer<typeof measurementImportV1>

// ---------------------------------------------------------------------------------------------------------------------
// M14: water and weather, READ-ONLY (ADR-102). A run and a fault are facts of the field; resolving a fault, correcting a run and setting the club's parameters are intents. A run is a RECORD of what happened:
// there is no field that could carry an instruction. Mirrors core.command_type.payload_schema.
// ---------------------------------------------------------------------------------------------------------------------
export const IRRIGATION_RUN_KINDS = ['program', 'manual', 'test'] as const
export const IRRIGATION_FAULT_KINDS = ['leak', 'no_run', 'low_pressure', 'head_blocked', 'valve_stuck', 'controller_alarm', 'flow_alarm', 'other'] as const
const numOrNull = z.number().nullable()
export const irrigationRecordRunV1 = z.strictObject({
  run_id: uuid, area_id: uuid, kind: z.enum(IRRIGATION_RUN_KINDS), station_id: uuidOrNull.optional(), started_at: instant.optional(),
  duration_min: numOrNull.optional(), depth_mm: numOrNull.optional(), volume_m3: numOrNull.optional(), note: text300.optional(),
})
export const irrigationCorrectRunV1 = z.strictObject({ run_id: uuid, corrects_run_id: uuid, duration_min: numOrNull.optional(), depth_mm: numOrNull.optional(), volume_m3: numOrNull.optional(), reason: z.string().min(1).max(300) })
export const irrigationFaultRaiseV1 = z.strictObject({
  fault_id: uuid, area_id: uuidOrNull.optional(), station_id: uuidOrNull.optional(), kind: z.enum(IRRIGATION_FAULT_KINDS), description: z.string().min(1).max(300), observed_at: instant.optional(),
})
export const irrigationFaultResolveV1 = z.strictObject({ fault_id: uuid, note: text300.optional() })
export const WATER_PROFILE_SCOPES = ['site', 'area_type'] as const
export const waterSetProfileV1 = z.strictObject({
  profile_id: uuid, scope: z.enum(WATER_PROFILE_SCOPES), area_type_code: z.string().regex(/^[a-z][a-z0-9_]{1,30}$/).nullable().optional(),
  kc: numOrNull.optional(), root_zone_mm: numOrNull.optional(), precip_mm_h: numOrNull.optional(), latitude: numOrNull.optional(), gdd_base_c: numOrNull.optional(), note: text300.optional(),
})
export const waterUpdateProfileV1 = z.strictObject({
  profile_id: uuid, expected_revision: z.number().int().min(1),
  set: z.strictObject({ kc: numOrNull.optional(), root_zone_mm: numOrNull.optional(), precip_mm_h: numOrNull.optional(), latitude: numOrNull.optional(), gdd_base_c: numOrNull.optional(), note: text300.optional(), archived: z.boolean().optional() })
    .refine((o) => Object.keys(o).length > 0),
})
export type IrrigationRecordRunV1 = z.infer<typeof irrigationRecordRunV1>
export type IrrigationCorrectRunV1 = z.infer<typeof irrigationCorrectRunV1>
export type IrrigationFaultRaiseV1 = z.infer<typeof irrigationFaultRaiseV1>
export type IrrigationFaultResolveV1 = z.infer<typeof irrigationFaultResolveV1>
export type WaterSetProfileV1 = z.infer<typeof waterSetProfileV1>
export type WaterUpdateProfileV1 = z.infer<typeof waterUpdateProfileV1>

// ---------------------------------------------------------------------------------------------------------------------
// M17: the club's look (ADR-107). The logo is named by its hash like a photo, uploaded by whoever may set it, then confirmed. Office work: online, no projector. Mirrors core.command_type.payload_schema.
// ---------------------------------------------------------------------------------------------------------------------
export const LOGO_MIMES = ['image/png', 'image/jpeg', 'image/webp'] as const
export const LOGO_MAX_BYTES = 2_097_152
export const brandingSetLogoV1 = z.strictObject({ sha256: z.string().regex(/^[0-9a-f]{64}$/), bytes: z.number().int().min(1).max(LOGO_MAX_BYTES), mime: z.enum(LOGO_MIMES), expected_revision: z.number().int().min(1).optional() })
export const brandingConfirmLogoV1 = z.strictObject({ file_id: uuid })
export type BrandingSetLogoV1 = z.infer<typeof brandingSetLogoV1>
export type BrandingConfirmLogoV1 = z.infer<typeof brandingConfirmLogoV1>

// ---------------------------------------------------------------------------------------------------------------------
// M19: who is where on the course (ADR-110). A phone shares its position ONLY while its person's work session is RUNNING (between start or resume and pause or finish),
// every LIVE_POSITION_EVERY_S seconds while the app is open; the server keeps ONE current position per running session and clears it when the session pauses or ends
// (nothing is kept after the session). The coordinates are redacted from the command log (sensitive_paths). Everybody who works on the course sees the positions
// (api.live_positions), never stored on a device. A repeat, a stale position or a session that is not running is silently ignored. Mirrors core.command_type.payload_schema.
// ---------------------------------------------------------------------------------------------------------------------
export const LIVE_POSITION_EVERY_S = 180
/** a position older than this is not shown (the phone was closed, or lost its signal) */
export const LIVE_POSITION_MAX_AGE_S = 900
export const workReportPositionV1 = z.strictObject({
  session_id: uuid, lat: z.number().min(-90).max(90), lon: z.number().min(-180).max(180), accuracy_m: z.number().min(0).max(10000), at: instantText,
})
export type WorkReportPositionV1 = z.infer<typeof workReportPositionV1>
/** One row of api.live_positions(p_org, p_site): a person on a running session of the site, with the position their phone last shared. */
export interface LivePosition {
  person_id: string; display_name: string; session_id: string; job_id: string | null; work_order_id: string | null
  title: { fr?: string; en?: string } | null; asset_id: string | null; asset_tag: string | null
  lat: number; lon: number; accuracy_m: number; at: string
}

// ---------------------------------------------------------------------------------------------------------------------
// M11 (ARCHITECTURE 16.2): how long each kind of record is kept is the club's decision. The direction (retention.manage) validates a proposed period or changes it:
// an intent, online only behind a fresh second factor (a shorter period erases older records the next night), no projector. The period is ISO 8601 in years,
// months and days (1 day to 30 years, checked by the server: RETENTION_PERIOD_INVALID); an unknown category is RETENTION_CATEGORY_UNKNOWN. Mirrors core.command_type.payload_schema.
// ---------------------------------------------------------------------------------------------------------------------
export const RETENTION_PERIOD_RE = /^P([0-9]{1,2}Y([0-9]{1,3}M)?([0-9]{1,5}D)?|[0-9]{1,3}M([0-9]{1,5}D)?|[0-9]{1,5}D)$/
export const retentionSetPolicyV1 = z.strictObject({ category: z.string().regex(/^[a-z][a-z_]{2,40}$/), period: z.string().regex(RETENTION_PERIOD_RE), validated: z.boolean() })
export type RetentionSetPolicyV1 = z.infer<typeof retentionSetPolicyV1>

// ---------------------------------------------------------------------------------------------------------------------
// M21 (the owner's answer of 2026-10-23: no GPS): whether the app takes the phones' positions at all is a setting of the site (core.site.location_mode), OFF unless the
// club turns it on. The direction (location.manage) turns it off or on: an intent, online only behind a fresh second factor, no projector. Off, the server stores no
// position whatever the report's version (LOCATION_OFF) and api.live_positions shows nobody; turning a site off wipes its positions at once. Mirrors
// core.command_type.payload_schema (supabase/migrations/20261123100100_location_mode.sql).
// ---------------------------------------------------------------------------------------------------------------------
export const LOCATION_MODES = ['off', 'on'] as const
export type LocationMode = (typeof LOCATION_MODES)[number]
/** core.site.location_mode's default: a club starts without location (pgTAP 53 and the privacy notice's fixture hold it). */
export const LOCATION_MODE_DEFAULT: LocationMode = 'off'
export const siteSetLocationModeV1 = z.strictObject({ mode: z.enum(LOCATION_MODES) })
export type SiteSetLocationModeV1 = z.infer<typeof siteSetLocationModeV1>

// ---------------------------------------------------------------------------------------------------------------------
// M20 (the staff register): a fonction is club data apart from the access role; an employment is a period of site-local days. Mirrors core.command_type.payload_schema
// (supabase/migrations/20261120100000_staff_register.sql, whose header says every rule and refusal). No projector: hire, change of fonction and departure are Y*
// (useSensitive().command: nonce, sendAndWait), the fonctions and the planned last day are office intents (sendAndWait).
// ---------------------------------------------------------------------------------------------------------------------
/** job_title.define v1 (person.manage): create a fonction (code, label and access_role_code required) or change an existing one's label, access role, order or
 *  archive state with expected_revision (REVISION_CONFLICT otherwise). The code never changes; the access role is never 'kiosk' (ROLE_UNKNOWN). */
export const jobTitleDefineV1 = z.strictObject({
  job_title_id: uuid,
  expected_revision: z.number().int().min(1).optional(),
  code: z.string().regex(/^[a-z0-9][a-z0-9_]{0,39}$/).optional(),
  label: z.strictObject({ fr: z.string().min(1).max(60), en: z.string().min(1).max(60) }).optional(),
  access_role_code: z.string().min(1).max(40).optional(),
  sort_order: z.number().int().min(0).max(10000).optional(),
  archived: z.boolean().optional(),
}).refine((o) => Object.keys(o).length >= 2, 'at least one field besides the id')
/** person.hire v1 (Y*, member.manage): a new person (with display_name, redacted in the log) or one of the club, a fonction and a first day (31 days back to 366 ahead). */
export const personHireV1 = z.strictObject({
  person_id: uuid, display_name: z.string().min(1).max(120).optional(), preferred_locale: z.enum(LOCALES_M5).optional(),
  job_title_id: uuid, first_day: isoDay, planned_last_day: isoDay.optional(),
})
/** person.change_job v1 (Y*, member.manage): the next period from from_day (after the open one's first day); the access role follows the fonction, a role granted by hand never moves. */
export const personChangeJobV1 = z.strictObject({ person_id: uuid, job_title_id: uuid, from_day: isoDay, planned_last_day: isoDay.optional() })
/** person.set_planned_last_day v1 (person.manage, not Y*): the open period's reminder; null clears it. */
export const personSetPlannedLastDayV1 = z.strictObject({ person_id: uuid, planned_last_day: isoDay.nullable() })
/** person.depart v1 (Y*, member.manage): the last day; every role in the club ends at the local midnight after it (now, for a past day). The answer's `future_assignments`. */
export const personDepartV1 = z.strictObject({ person_id: uuid, last_day: isoDay })
/** The result of person.depart@1 the device reads to offer the reassignment: the person's live assignments on jobs after the last day (planning is never cancelled). */
export interface DepartFutureAssignments { count: number; days: string[] }
/** job_title.define v2 (M23 item 28): v1 plus `in_rotation` (whether the people holding the fonction are proposed by the rotation assistant). A creation
 *  without it takes the rule by fonction code (superintendent and mecanicien out, the rest in); a change without it keeps the value; v1 stays and keeps it. */
export const jobTitleDefineV2 = z.strictObject({
  job_title_id: uuid,
  expected_revision: z.number().int().min(1).optional(),
  code: z.string().regex(/^[a-z0-9][a-z0-9_]{0,39}$/).optional(),
  label: z.strictObject({ fr: z.string().min(1).max(60), en: z.string().min(1).max(60) }).optional(),
  access_role_code: z.string().min(1).max(40).optional(),
  sort_order: z.number().int().min(0).max(10000).optional(),
  archived: z.boolean().optional(),
  in_rotation: z.boolean().optional(),
}).refine((o) => Object.keys(o).length >= 2, 'at least one field besides the id')
export type JobTitleDefineV1 = z.infer<typeof jobTitleDefineV1>
export type JobTitleDefineV2 = z.infer<typeof jobTitleDefineV2>
export type PersonHireV1 = z.infer<typeof personHireV1>
export type PersonChangeJobV1 = z.infer<typeof personChangeJobV1>
export type PersonSetPlannedLastDayV1 = z.infer<typeof personSetPlannedLastDayV1>
export type PersonDepartV1 = z.infer<typeof personDepartV1>

// M20 part B: the information sheet (fiche de renseignements). Mirrors core.command_type.payload_schema (supabase/migrations/20261120100200_staff_file.sql, whose header
// says every rule and refusal). Y* (contact.manage: useSensitive().command, nonce, sendAndWait), no projector; every value is in sensitive_paths and the device keeps
// only the person id once answered (apps/web/src/sync/transient.ts).
const fileText = z.string().max(10).nullable().optional()
/** person_file.set v1 (Y*, contact.manage): an absent key keeps, null (or a blank text) clears; `emergency` (0..2) REPLACES the person's list; the birth date gives
 *  an age of 14 to 100 on the site's day (BIRTH_DATE_IMPLAUSIBLE). Nothing but the person is refused. */
export const personFileSetV1 = z.strictObject({
  person_id: uuid,
  birth_date: isoDay.nullable().optional(),
  contact: z.strictObject({ email: z.string().max(200).nullable().optional(), phone: z.string().max(40).nullable().optional(), address: z.string().max(300).nullable().optional() }).optional(),
  emergency: z.array(z.strictObject({ name: z.string().min(1).max(120), relationship: z.string().min(1).max(60), phone: z.string().min(1).max(40) })).max(2).optional(),
  clothing_top: fileText, clothing_bottom: fileText, shoe_size: fileText,
}).refine((o) => Object.keys(o).length >= 2, 'at least one field besides the person')
export type PersonFileSetV1 = z.infer<typeof personFileSetV1>
/** What api.read_person_file answers (audited, online only, never cached on a device). */
export interface PersonFile {
  person_id: string; erased: boolean; birth_date: string | null; clothing_top: string | null; clothing_bottom: string | null; shoe_size: string | null; revision: number | null
  contact: { email: string | null; phone: string | null; address: string | null }
  emergency: Array<{ rank: number; name: string; relationship: string; phone: string }>
}
/** What api.read_emergency_contacts answers: every person working at the site on its local day, with their emergency contacts (audited, for the printable sheet). */
export interface EmergencySheet {
  site_id: string; day: string
  people: Array<{ person_id: string; display_name: string; job_title_id: string | null; contacts: PersonFile['emergency'] }>
}

export const COMMANDS = {
  'person.create@1': personCreateV1,
  'asset.register@1': assetRegisterV1,
  'asset_type.define_local@1': assetTypeDefineLocalV1,
  'asset.update@1': assetUpdateV1,
  'asset.retire@1': assetRetireV1,
  'external_id.map@1': externalIdMapV1,
  'external_id.unmap@1': externalIdUnmapV1,
  'capability.declare@1': capabilityDeclareV1,
  'component.mount@1': componentMountV1,
  'component.unmount@1': componentUnmountV1,
  'asset.import_stage@1': assetImportStageV1,
  'asset.import_commit@1': assetImportCommitV1,
  // asset.import_commit v2 = v1 plus an opening engine-hours meter for every created asset whose row had hours: same payload
  'asset.import_commit@2': assetImportCommitV1,
  'meter.define_slot@1': meterDefineSlotV1,
  'meter.record@1': meterRecordV1,
  'meter.record_estimate@1': meterRecordEstimateV1,
  'meter.correct@1': meterCorrectV1,
  'meter.replace@1': meterReplaceV1,
  'meter.review@1': meterReviewV1,
  'service_plan.publish@1': servicePlanPublishV1,
  'service_event.record@1': serviceEventRecordV1,
  'checklist.publish@1': checklistPublishV1,
  'inspection.submit@1': inspectionSubmitV1,
  'defect.raise@1': defectRaiseV1,
  'defect.triage@1': defectTriageV1,
  'defect.downgrade@1': defectDowngradeV1,
  'asset.restrict@1': assetRestrictV1,
  'asset.release@1': assetReleaseV1,
  'workorder.open@1': workorderOpenV1,
  'workorder.update@1': workorderUpdateV1,
  'workorder.mark_repaired@1': workorderMarkRepairedV1,
  'workorder.cancel@1': workorderCancelV1,
  'person.update_profile@1': personUpdateProfileV1,
  'person.update_own_profile@1': personUpdateOwnProfileV1,
  'person.link_login@1': personLinkLoginV1,
  'member.grant_role@1': memberGrantRoleV1,
  'member.revoke_role@1': memberRevokeRoleV1,
  'mfa.approve_factor@1': mfaApproveFactorV1,
  'mfa.revoke_factor@1': mfaRevokeFactorV1,
  'device.enroll@1': deviceEnrollV1,
  'device.revoke@1': deviceRevokeV1,
  'review.flag_device_commands@1': reviewFlagDeviceCommandsV1,
  'qualification_type.define@1': qualificationTypeDefineV1,
  'qualification.record@1': qualificationRecordV1,
  'qualification.revoke@1': qualificationRevokeV1,
  'qualification.override@1': qualificationOverrideV1,
  'asset_type_requirement.set@1': assetTypeRequirementSetV1,
  'asset_type_requirement.clear@1': assetTypeRequirementClearV1,
  'pay_rate.set@1': payRateSetV1,
  'person_contact.set@1': personContactSetV1,
  'person.pseudonymise@1': personPseudonymiseV1,
  'area.create@1': areaCreateV1,
  'area.update@1': areaUpdateV1,
  'area.revise@1': areaReviseV1,
  'area.archive@1': areaArchiveV1,
  'infra.create@1': infraCreateV1,
  'infra.update@1': infraUpdateV1,
  'infra.revise@1': infraReviseV1,
  'infra.retire@1': infraRetireV1,
  'map_layer.publish@1': mapLayerPublishV1,
  'task_type.define@1': taskTypeDefineV1,
  'task_type.update@1': taskTypeUpdateV1,
  'document.publish_version@1': documentPublishVersionV1,
  'job_template.publish@1': jobTemplatePublishV1,
  'recurrence.publish@1': recurrencePublishV1,
  'recurrence.archive@1': recurrenceArchiveV1,
  'job.create@1': jobCreateV1,
  'job.update@1': jobUpdateV1,
  'job.reschedule@1': jobRescheduleV1,
  'job.cancel@1': jobCancelV1,
  'job.copy_day@1': jobCopyDayV1,
  'assignment.create@1': assignmentCreateV1,
  'assignment.remove@1': assignmentRemoveV1,
  'availability.set@1': availabilitySetV1,
  'job.start@1': jobStartV1,
  'job.pause@1': jobPauseV1,
  'job.resume@1': jobResumeV1,
  'job.switch_asset@1': jobSwitchAssetV1,
  'job.finish@1': jobFinishV1,
  'work.manual_entry@1': workManualEntryV1,
  'work.correct@1': workCorrectV1,
  'work.review@1': workReviewV1,
  'review.resolve@1': reviewResolveV1,
  'attachment.register@1': attachmentRegisterV1,
  'attachment.confirm@1': attachmentConfirmV1,
  'attachment.link@1': attachmentLinkV1,
  'supplier.define@1': supplierDefineV1,
  'supplier.update@1': supplierUpdateV1,
  'product.define@1': productDefineV1,
  'product.update@1': productUpdateV1,
  'product.revise@1': productReviseV1,
  'batch.record@1': batchRecordV1,
  'workorder.add_part@1': workorderAddPartV1,
  'workorder.remove_part@1': workorderRemovePartV1,
  'measurement.record@1': measurementRecordV1,
  'measurement.correct@1': measurementCorrectV1,
  'measurement.set_target@1': measurementSetTargetV1,
  'measurement.update_target@1': measurementUpdateTargetV1,
  'measurement.import@1': measurementImportV1,
  'irrigation.record_run@1': irrigationRecordRunV1,
  'irrigation.correct_run@1': irrigationCorrectRunV1,
  'irrigation.fault_raise@1': irrigationFaultRaiseV1,
  'irrigation.fault_resolve@1': irrigationFaultResolveV1,
  'water.set_profile@1': waterSetProfileV1,
  'water.update_profile@1': waterUpdateProfileV1,
  'branding.set_logo@1': brandingSetLogoV1,
  'branding.confirm_logo@1': brandingConfirmLogoV1,
  'work.report_position@1': workReportPositionV1,
  // work.report_position v2 = v1 + only the device that opened the running stretch shares it (M19b, ADR-111): same payload
  'work.report_position@2': workReportPositionV1,
  // component.mount v2 = v1 + the late-use scan when the mounted component is restricted: same payload
  'component.mount@2': componentMountV1,
  'retention.set_policy@1': retentionSetPolicyV1,
  // M21: location off or on at the site
  'site.set_location_mode@1': siteSetLocationModeV1,
  // M20: the staff register
  'job_title.define@1': jobTitleDefineV1,
  // M23 item 28: v2 = v1 + in_rotation (the rotation pool follows the fonction)
  'job_title.define@2': jobTitleDefineV2,
  'person.hire@1': personHireV1,
  'person.change_job@1': personChangeJobV1,
  'person.set_planned_last_day@1': personSetPlannedLastDayV1,
  'person.depart@1': personDepartV1,
  'person_file.set@1': personFileSetV1,
  // ---- M16a (docs/M16.md rule 8): each part's schemas in its own file under src/, registered here by Z's close; Z's opening registers its own
  // task_type.update v2 = v1 + set.tags (the words the code reads: 'pins', 'habitat'; taskTypes.ts)
  'task_type.update@2': taskTypeUpdateV2,
  // M16-B1: the course notices (courseStatus.ts; four office intents under course_status.manage / .publish)
  'course_notice.write@1': courseNoticeWriteV1,
  'course_notice.update@1': courseNoticeUpdateV1,
  'course_notice.retire@1': courseNoticeRetireV1,
  'course_notice.approve@1': courseNoticeApproveV1,
  // M16-J: the measured cutting setup (cuttingSetup.ts; a field fact under setup.record, kiosk-allowed by rule 9)
  'cutting_setup.record@1': cuttingSetupRecordV1,
  // M16-C: the flags (pins.ts): one tap per sector from the job is a field fact under flag.record; corrections, exclusions and the club's words are the office's (flag.manage)
  'pin.place@1': pinPlaceV1,
  'pin.correct@1': pinCorrectV1,
  'pin_exclusion.set@1': pinExclusionSetV1,
  'pin_exclusion.close@1': pinExclusionCloseV1,
  'flag_settings.set@1': flagSettingsSetV1,
  // M16-L1: water, the records (irrigation.ts). A fault seen, given and followed are intents of whoever repairs / plans; record_run v2 = v1 + cycles and
  // soak (a field fact, projectors.water2.ts), correct_run v2 likewise (an intent); water.update_profile v2 = v1 + set.constraints (an intent)
  'irrigation.fault_acknowledge@1': irrigationFaultAcknowledgeV1,
  'irrigation.fault_assign@1': irrigationFaultAssignV1,
  'irrigation.fault_link_job@1': irrigationFaultLinkJobV1,
  'irrigation.record_run@2': irrigationRecordRunV2,
  'irrigation.correct_run@2': irrigationCorrectRunV2,
  'water.update_profile@2': waterUpdateProfileV2,
  // M16-N: an observation is a field fact under the notebook's permission (measurement.record; projectors.observations.ts); naming its follow-up job is the planners' intent (job.plan)
  'observation.record@1': observationRecordV1,
  'observation.link_job@1': observationLinkJobV1,
  // M16a review, finding 24 (procedures.ts): a procedure version's pictures are the publisher's intent (procedure.publish; the version and one to six files with
  // captions), no projector; the generic attachment path no longer links a picture to a document version
  'document.illustrate@1': documentIllustrateV1,
  // ---- M15a (docs/M15.md rule 10): each part's schemas in its own file under src/, registered here by the part (additive lines under this hook, as M16a's parts did,
  // listed in docs/m15-requests/<part>.md) or by Z's close. Z's opening registers nothing: the attachment kind above is a value of @1, not a command.
  // M15-A (stock.ts): the places and the storeroom's acts are intents under stock.manage; a consumption typed at the finish, a fill-up at the pump and a return are
  // facts under stock.record (kiosk-allowed by rule 8); add_part@2 / remove_part@2 = @1 + the stock effect; product.update@2 = @1 + min_qty_base
  'stock_location.define@1': stockLocationDefineV1,
  'stock_location.archive@1': stockLocationArchiveV1,
  'stock.receive@1': stockReceiveV1,
  'stock.consume@1': stockConsumeV1,
  'stock.refuel@1': stockRefuelV1,
  'stock.return@1': stockReturnV1,
  'stock.waste@1': stockWasteV1,
  'stock.transfer@1': stockTransferV1,
  'stock.reverse@1': stockReverseV1,
  'workorder.add_part@2': workorderAddPartV2,
  'workorder.remove_part@2': workorderRemovePartV2,
  'product.update@2': productUpdateV2,
  // M15-C (treatments.ts, nutrients.ts): the plan, its change while a draft and its cancellation are intents under treatment.manage; the verification an intent under
  // treatment.verify (no second factor); treatment.record_application@1 is the FACT of the field under treatment.record (kiosk-allowed by rule 8); a correction an intent
  // under treatment.manage; qualification_type.define@2 = @1 + kind (the Certiphyto); product.revise@2 = @1 with the declared composition
  'treatment.plan@1': treatmentPlanV1,
  'treatment.update_plan@1': treatmentUpdatePlanV1,
  'treatment.cancel_plan@1': treatmentCancelPlanV1,
  'treatment.verify@1': treatmentVerifyV1,
  'treatment.record_application@1': treatmentRecordApplicationV1,
  'treatment.correct@1': treatmentCorrectV1,
  'qualification_type.define@2': qualificationTypeDefineV2,
  'product.revise@2': productReviseV2,
  // ---- M16b (docs/M16.md rule 8): each part's schemas in its own file under src/, registered here by the part (additive lines under this hook, listed in
  // docs/m16-requests/<part>.md, with the key in contracts.test.ts's pinned list, the fixtures in fixtures/commands.json and the line in sync/projectors.ts) or by
  // Z's close. Z's b opening registers nothing: the two attachment kinds are values of @1, not commands. D: measurement.record@2; E: infra.record_maintenance@1;
  // G: incident.report@1 and the three intents; I2: procedure.acknowledge@1, document.set_ask_reading@1 (H brings no command; L2 and B2 wait for the owner)
  // M16-E (infra.ts): what was done on an infrastructure feature is a field fact under infra.maintain (the crew and the mechanic, the direction by the rule; kiosk-allowed)
  'infra.record_maintenance@1': infraRecordMaintenanceV1,
  // M16-D (measurementsV2.ts): a reading with the cell of the club's grid (a bunker walk) = @1 + position_label; a field fact under measurement.record, kiosk-allowed
  // (rule 9); the measurement projector reads both versions
  'measurement.record@2': measurementRecordV2,
  // M16-I2 (procedures.ts): "J'ai lu cette version" is a field fact under procedure.read (own person only, kiosk-allowed; projectors.ack.ts); "demander une lecture" is the
  // publisher's intent (procedure.publish, no projector)
  'procedure.acknowledge@1': procedureAcknowledgeV1,
  'document.set_ask_reading@1': documentSetAskReadingV1,
  // M16-G (incidents.ts): an incident reported is a FACT and a safety fact under incident.report (offline, kiosk-allowed, salvaged when it fails; its projection a stub
  // without the text, projectors.incidents.ts); giving it to someone, an action and the closure are Y* intents of the direction (incident.manage, no projector)
  'incident.report@1': incidentReportV1,
  'incident.assign@1': incidentAssignV1,
  'incident.add_action@1': incidentAddActionV1,
  'incident.close@1': incidentCloseV1,
  // ---- M15b (docs/M15.md rule 10): likewise. B: stock.count_open@1, stock.count_line@1, stock.count_close@1; V: attachment.register@2; E1: asset_financial.set@1,
  // asset_cost.record@1
  // M15-B (stockCount.ts): opening and closing a count are the storeroom's intents under stock.manage; a line counted is a FACT under stock.record (offline in the
  // storeroom, kiosk-allowed by rule 8; projectors.count.ts)
  'stock.count_open@1': stockCountOpenV1,
  'stock.count_line@1': stockCountLineV1,
  'stock.count_close@1': stockCountCloseV1,
  // M15-V (documents.ts): attachment.register@2 = @1 + the bucket (a FACT like @1; the entity kinds are the one list above); the vault's publisher's two intents
  // under procedure.publish: a version with its file (publish_version@2 = @1 + file, the text optional) and a version retired
  'attachment.register@2': attachmentRegisterV2Of(ATTACHMENT_ENTITY_TYPES),
  'document.publish_version@2': documentPublishVersionV2Of(DOCUMENT_KINDS),
  'document.retire_version@1': documentRetireVersionV1,
  // M15-E1 (finance.ts): a machine's financials, a cost recorded and a cost voided are Y* intents of the direction (finance.manage: aal2, a server nonce, no
  // projector; every amount in sensitive_paths, the outbox row kept with its ids only once answered: sync/transient.ts)
  'asset_financial.set@1': assetFinancialSetV1,
  'asset_cost.record@1': assetCostRecordV1,
  'asset_cost.void@1': assetCostVoidV1,
  // ---- M24 (docs/M24.md rule 12; the opening registers nothing): each part's schemas registered here by the part (additive lines under this hook, listed in
  // docs/m24-requests/<part>.md, with the key in contracts.test.ts's pinned list and the fixtures in fixtures/commands.json) or by Z's close. C: connector.define@1 and
  // connector.update@1 (intents of the direction, connector.manage); M: water.set_profile@2 and water.update_profile@3 (intents, water.manage); F: forecast.ingest@1,
  // kind 'system' — also in SYSTEM_COMMANDS below and its fixture set marked "system": true (never sent by a device)
  // M24-C (connectors.ts): defining a connector (disabled) and changing it (on/off, label, staleness, note, archive) are online intents of the direction
  // (connector.manage, never a workstation, no projector; no second factor and no server nonce since the owner's decision of 2026-10-28, 20261228100000)
  'connector.define@1': connectorDefineV1,
  'connector.update@1': connectorUpdateV1,
  // M24-M (irrigation.ts): the course's altitude for Penman-Monteith, on the site row: set_profile@2 = @1 + altitude_m, update_profile@3 = @2 + set.altitude_m
  // (intents, water.manage, sendAndWait, no projector; @1 and @2 stay for older builds)
  'water.set_profile@2': waterSetProfileV2,
  'water.update_profile@3': waterUpdateProfileV3,
  // M24-F (weather.ts): the prepared issue of a forecast file, ingested by the server's job through its door jobs.ingest_forecast as the club's system
  // person — kind 'system': never sent by a device (the dispatcher refuses it, COMMAND_SYSTEM_ONLY), listed in SYSTEM_COMMANDS below
  'forecast.ingest@1': forecastIngestV1,
  // ---- M25 (docs/M25.md rule 10; Z's opening): each part's schemas in its own file under src/ (importing primitives.ts, never this file), registered HERE by the part
  // (additive lines under this hook, listed in docs/m25-requests/<part>.md, with the key in contracts.test.ts's pinned list, the fixtures in fixtures/commands.json
  // and the line in sync/projectors.ts) or by Z's close. F: job.confirm_settings@1 (settingsConfirmation.ts, a field fact under job.execute, kiosk-allowed);
  // L: area.set_play_bearing@1 (playBearing.ts, an intent under area.edit)
  // M25-Z (taskTypes.ts, settingLooks.ts): the pictures of a type's choices. define@2 = @1 + tags + choice_looks; update@3 = @2 + set.choice_looks (intents,
  // template.publish, no projector; @1 and @2 stay for older builds and keep the column)
  'task_type.define@2': taskTypeDefineV2,
  'task_type.update@3': taskTypeUpdateV3,
  // M25-L (playBearing.ts): the club's correction of an area's line of play (an intent under area.edit, no projector; used only to turn a drawing)
  'area.set_play_bearing@1': areaSetPlayBearingV1,
  // M25-F (settingsConfirmation.ts): what was really done, confirmed at the finish (a FACT under job.execute, kiosk-allowed; projector sync/projectors.settings.ts)
  'job.confirm_settings@1': jobConfirmSettingsV1,
} as const

export type CommandKey = keyof typeof COMMANDS

/** M24 (docs/M24.md D, ADR-125; the opening): the command types of kind 'system'. A system command is run by its own jobs.* door of the server as the club's system
 *  person (no core.command_log row), and the dispatcher refuses it from every client with COMMAND_SYSTEM_ONLY, before the workstation's and the permission checks.
 *  It is registered in COMMANDS like any command, so that `npm run test:contracts` holds its payload to core.command_type.payload_schema (fixtures and random
 *  payloads); it is listed HERE, and its fixture set in fixtures/commands.json carries `"system": true`, and the check holds this list, that mark and
 *  core.command_type.kind = 'system' to one another (fixtures.test.ts holds the first two). No device may queue one (`isSystemCommand`). F adds 'forecast.ingest@1'
 *  (an additive line under this hook, listed in docs/m24-requests/f.md). */
export const SYSTEM_COMMANDS: readonly CommandKey[] = [
  // M24-F: the forecast's ingest (jobs.ingest_forecast)
  'forecast.ingest@1',
]
export const isSystemCommand = (key: string): boolean => (SYSTEM_COMMANDS as readonly string[]).includes(key)

/** M24 (docs/M24.md A and E, rule 3; the opening): what each connector kind's reader sends its jobs.* door — the zod twin of ref.connector_kind.input_schema (C's
 *  column; a kind whose input_schema is null has no reader and is not offered: CONNECTOR_KIND_UNKNOWN). `npm run test:contracts` holds each entry to the stored
 *  schema (the kind's fixture files and random payloads built from it, as for the commands) and fails on a kind with a schema and no twin, or a twin with no stored
 *  schema. F adds `dwd_mosmix_l: forecastFileV1` (weather.ts) under this hook; S adds the station kinds when it is built. */
export const CONNECTOR_INPUT_SCHEMAS: Readonly<Record<string, z.ZodType>> = {
  // M24-F: DWD MOSMIX_L, the reader's file payload (weather.ts; packages/jobs/src/weather/providers.ts)
  dwd_mosmix_l: forecastFileV1,
}
export type CommandName = CommandKey extends `${infer N}@${number}` ? N : never
export type PayloadOf<K extends CommandKey> = z.infer<(typeof COMMANDS)[K]>

export const commandKey = (name: string, version: number): string => `${name}@${version}`
